import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { Token as Color } from '../../../../export/brand0/colors';
import { DEFAULT_MODULOR_DOMAIN } from '../constants/modulorDomain';
import { Counter } from '../counter';
import { BodyAttrsObserver } from '../lib/body-mutation-observer';
import { fetchRetry } from '../lib/fetch-retry';
import { PLACEHOLDER } from '../lib/placeholder';

import { getUrlByAssetName } from './lib/get-url-by-asset-name';
import styles from './icon.scss';

const NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

export const DEFAULT_SIZE = 24;

const CACHE = new Map();

export type IconProps = {
  name?: string;
  src?: string;
  placeholder?: string;
  fill?: Color;
  size?: number;
  disabled?: boolean;
  background?: Color;
  count?: number;
  dataTestId?: string;
  preloadIcon?: string;
};

type Props = RefAttributes<HTMLSpanElement & HTMLButtonElement> &
  HTMLAttributes<HTMLSpanElement | HTMLButtonElement> &
  IconProps;

const COMPONENT_ID = 'modulor-icon';

export const Icon: ForwardRefExoticComponent<Props> = forwardRef<
  HTMLSpanElement & HTMLButtonElement,
  Props
>(
  (
    {
      name,
      src,
      placeholder,
      fill,
      size = DEFAULT_SIZE,
      disabled,
      background,
      count,
      className,
      style,
      children,
      onClick,
      preloadIcon,
      dataTestId = COMPONENT_ID,
      ...rest
    },
    ref,
  ) => {
    const [iconData, setIconData] = useState<string | null>('');
    const [placeholderData, setPlaceholderData] = useState<string | null>('');
    const [theme, setTheme] = useState(document?.body.dataset.themeName || null);
    const [modulorDomain, setModulorDomain] = useState(
      document?.body.dataset.modulorDomain || DEFAULT_MODULOR_DOMAIN,
    );
    const controller = useRef<AbortController | null>(null);
    const colorIcon = fill != null && !disabled ? `var(--${fill})` : undefined;
    const backgroundColor = background != null ? background : undefined;
    const classes = classNames(styles.container, className, {
      [styles.clickable]: onClick != null || rest.onMouseDown != null,
    });

    const localStyle = {
      width: size,
      height: size,
      color: colorIcon,
      backgroundColor,
      ...style,
    };

    const requestRemotePlaceholder = useCallback(async () => {
      if (placeholder == null || placeholder == '' || theme == null) {
        setPlaceholderData(PLACEHOLDER);
        return;
      }

      setPlaceholderData(null);

      const isName = NAME_PATTERN.test(placeholder);
      const url = isName ? getUrlByAssetName(placeholder, theme, modulorDomain) : placeholder;

      try {
        if (!CACHE.has(url)) {
          CACHE.set(url, fetch(url));
        }
        const response = await CACHE.get(url);

        if (response.ok) {
          const requestedPlaceholder = await response
            .clone()
            .text()
            .then((p) => (p.startsWith('<svg') ? p : null));

          setPlaceholderData(requestedPlaceholder);
        }
        setPlaceholderData(PLACEHOLDER);
      } catch (error) {
        return;
      }
    }, [placeholder, theme, modulorDomain]);

    const fetchIconByName = useCallback(
      async (iconName: string, options?) => {
        const url = src || getUrlByAssetName(iconName, theme, modulorDomain);

        try {
          if (!CACHE.has(url) && !options.preloading) {
            CACHE.set(url, fetchRetry({ url, options }));
          }

          const response = await CACHE.get(url);

          if (response.ok) {
            const icon = await response.clone().text();

            if (!options.preloading) setIconData(icon);
          } else {
            await requestRemotePlaceholder();
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }
          controller.current = null;

          await requestRemotePlaceholder();
        }
      },
      [requestRemotePlaceholder, src, theme, modulorDomain],
    );

    const requestIcon = useCallback(async () => {
      if (name == null || name === '' || theme == null) {
        return;
      }

      setIconData('');

      if (controller.current != null) {
        controller.current.abort();
      }

      controller.current = new AbortController();
      const { signal } = controller.current;

      await fetchIconByName(name, { ...signal, preloading: false });
    }, [fetchIconByName, name, theme]);

    const requestIconPreload = useCallback(async () => {
      if (!preloadIcon) {
        return;
      }

      await fetchIconByName(preloadIcon, { preloading: true });
    }, [fetchIconByName, preloadIcon]);

    useEffect(() => {
      requestIconPreload();
    }, [preloadIcon, theme]);

    useEffect(() => {
      requestIcon();
    }, [name, requestIcon, src, theme]);

    useEffect(() => {
      return BodyAttrsObserver.listen({
        'data-theme-name': setTheme,
        'data-modulor-domain': setModulorDomain,
      });
    }, []);

    return onClick != null || rest.onMouseDown != null ? (
      <button
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        ref={ref}
        type="button"
        className={classes}
        style={localStyle}
        onClick={onClick}
        disabled={disabled}
        {...rest}>
        <span
          className={styles.icon}
          data-testid={`${COMPONENT_ID}-${name}`}
          dangerouslySetInnerHTML={{
            __html: iconData || placeholderData,
          }}
        />
        <span className={styles.counter}>
          {children == null ? (
            <Counter value={count} notEmpty dataTestId={`${dataTestId}-counter`} />
          ) : (
            children
          )}
        </span>
      </button>
    ) : (
      <span
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        ref={ref}
        className={classes}
        style={localStyle}
        {...rest}>
        <span
          className={styles.icon}
          dangerouslySetInnerHTML={{
            __html: iconData || placeholderData,
          }}
        />
        <span className={styles.counter}>
          {children == null ? (
            <Counter value={count} notEmpty dataTestId={`${dataTestId}-counter`} />
          ) : (
            children
          )}
        </span>
      </span>
    );
  },
);

Icon.displayName = 'Icon';
